import { createAsyncThunk } from "@reduxjs/toolkit";
import apiConfig from "constants/apiConfig";
import { fetchSocialAccount, fetchTrackedPosts, fetchSocialAccountCreators, fetchSocialAccountContent } from "services/api/social-account";
import { AuthState } from "slices/auth/reducer";
import { ERROR } from "types";
import { SocialAccount } from "types/influencer";
import { SocialAccountContentItem, SocialAccountCreator, SocialAccountCreatorsFilters, SocialAccountState, TrackedPost, TrackedPostsFilters } from "types/social-account";
import { generateUniqueKey, getErrorMessage, isFresh } from "utils/helpers";

export const fetchSocialAccountThunk = createAsyncThunk<{socialAccountId: string, socialAccount: SocialAccount, cached?: boolean}, {socialAccountId: string, force?: boolean}, {rejectValue: string}>(
    'social-account/fetch',
    async ({socialAccountId, force=false}, {getState, rejectWithValue}) => {
        try {
            const state = getState() as {auth: AuthState, socialAccountState: SocialAccountState};
            const STALE_TIME = apiConfig.FETCH_SOCIAL_ACCOUNT.staleTime;

            if(!force && isFresh(state.socialAccountState.items?.[socialAccountId]?.last_fetched_at ?? null, STALE_TIME)){
                return {socialAccountId, socialAccount: state.socialAccountState.items?.[socialAccountId].account, cached: true}
            }


            const agencyId = state.auth.selectedAgencyId ?? '';
            if(!agencyId) throw new Error("No agency id selected!");
            const {social_account} = await fetchSocialAccount(agencyId, socialAccountId);

            return {socialAccountId, socialAccount: social_account}

        } catch (error) {
            return rejectWithValue(getErrorMessage(error, 'failed to fetch social account'));
        }
    }
)

export const fetchSocialAccountTrackedPostsThunk = createAsyncThunk<{socialAccountId: string, items: TrackedPost[], filters: TrackedPostsFilters, filterId: string, count: number}, {socialAccountId: string, filters: TrackedPostsFilters, force?: boolean}, {rejectValue: string}>(
    'social-account/tracked-posts',
    async ({socialAccountId, filters, force=false}, {getState, rejectWithValue}) => {
        try {
            const STALE_TIME = apiConfig.FETCH_SOCIAL_ACCOUNT_TRACKED_POSTS.staleTime;
            const filterId = generateUniqueKey(filters, ['page']);
            const state = getState() as { auth: AuthState, socialAccountState: SocialAccountState };
            if (filters.page === 1 && !force && isFresh(state.socialAccountState.trackedPosts.items[socialAccountId]?.filtered_lists?.[filterId]?.last_fetched_at ?? null, STALE_TIME)) {
                return rejectWithValue(ERROR.CACHED);
            }
            const agencyId = state.auth.selectedAgency?.uuid ?? '';
            const {count, items} = await fetchTrackedPosts(agencyId, socialAccountId, filters);
            return { items, filters, filterId, socialAccountId, count };
        } catch (error) {
            return rejectWithValue(getErrorMessage(error, 'failed to fetch social account tracked posts'));
        }
    }
);

export const fetchSocialAccountCreatorsThunk = createAsyncThunk<{socialAccountId: string, items: SocialAccountCreator[], filters: SocialAccountCreatorsFilters, filterId: string, count: number}, {socialAccountId: string, filters: SocialAccountCreatorsFilters, force?: boolean}, {rejectValue: string}>(
    'social-account/creators',
    async ({socialAccountId, filters, force=false}, {getState, rejectWithValue}) => {
        try {
            const STALE_TIME = apiConfig.FETCH_SOCIAL_ACCOUNT_CREATORS.staleTime;
            const filterId = generateUniqueKey(filters, ['page']);
            const state = getState() as { auth: AuthState, socialAccountState: SocialAccountState };
            if (filters.page === 1 && !force && isFresh(state.socialAccountState.creators.items[socialAccountId]?.filtered_lists?.[filterId]?.last_fetched_at ?? null, STALE_TIME)) {
                return rejectWithValue(ERROR.CACHED);
            }
            const agencyId = state.auth.selectedAgency?.uuid ?? '';
            const {count, items} = await fetchSocialAccountCreators(agencyId, socialAccountId, filters);
            return { items, filters, filterId, socialAccountId, count };
        } catch (error) {
            return rejectWithValue(getErrorMessage(error, 'failed to fetch social account creators'));
        }
    }
);

export const fetchSocialAccountContentThunk = createAsyncThunk<{socialAccountId: string, items: SocialAccountContentItem[], filters: SocialAccountCreatorsFilters, filterId: string, count: number}, {socialAccountId: string, filters: SocialAccountCreatorsFilters, force?: boolean}, {rejectValue: string}>(
    'social-account/content',
    async ({socialAccountId, filters, force=false}, {getState, rejectWithValue}) => {
        try {
            const STALE_TIME = apiConfig.FETCH_SOCIAL_ACCOUNT_CONTENT.staleTime;
            const filterId = generateUniqueKey(filters, ['page']);
            const state = getState() as { auth: AuthState, socialAccountState: SocialAccountState };
            if (filters.page === 1 && !force && isFresh(state.socialAccountState.content.items[socialAccountId]?.filtered_lists?.[filterId]?.last_fetched_at ?? null, STALE_TIME)) {
                return rejectWithValue(ERROR.CACHED);
            }
            const agencyId = state.auth.selectedAgency?.uuid ?? '';
            const {count, items} = await fetchSocialAccountContent(agencyId, socialAccountId, filters);
            return { items, filters, filterId, socialAccountId, count };
        } catch (error) {
            return rejectWithValue(getErrorMessage(error, 'failed to fetch social account content'));
        }
    }
);
